'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { ArrowLeft, Save, Send, CheckCircle, XCircle, Copy, Plus, Trash2, Leaf, ChevronDown, Search, AlertCircle, FileText, Eye, Edit2, Printer, Download } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import ToastContainer, { showToast } from '@/components/ui/Toast';
import { Quotation, LineItem, QuoteStatus, GSTType, Template } from '@/lib/types';
import { getQuotationById, upsertQuotation, generateQuoteNumber, generateId, getTemplates } from '@/lib/storage';
import { CATALOGUE, VALID_UNITS, VALID_GST_RATES } from '@/lib/catalogue';
import { formatINR, amountInWords, todayDDMMYYYY, addDaysDDMMYYYY, detectGSTType } from '@/lib/utils';
import QuotationPreview from './QuotationPreview';

const DEFAULT_TERMS = `Payment: 50% advance on order confirmation; balance before delivery/installation. All prices are exclusive of GST. Quote valid for 30 days from the date above. Goods once delivered and accepted cannot be returned. Transportation charges are additional unless stated. Terrace waterproofing is the client's responsibility unless explicitly included in scope. Subject to New Delhi jurisdiction.`;

const DEFAULT_NOTES = `All plants are nursery-grown and healthy at time of delivery. TGI is ISO 9001:2015 certified — quality guaranteed. A site visit is recommended before finalising quantities and scope. Warranty on irrigation hardware: 1 year from installation. AMC packages available separately. For queries, contact +91 96259 52842 or visit www.terracegardenindia.com.`;

function createEmptyLineItem(srNo: number): LineItem {
  return {
    id: `li-new-${Date.now()}-${srNo}`,
    srNo,
    description: '',
    hsn: '',
    qty: 1,
    unit: 'Nos',
    rate: 0,
    gstRate: 18,
    amount: 0,
    gstAmount: 0,
    total: 0,
  };
}

function computeLineItem(li: LineItem): LineItem {
  const amount = parseFloat((li.qty * li.rate).toFixed(2));
  const gstAmount = parseFloat((amount * li.gstRate / 100).toFixed(2));
  const total = parseFloat((amount + gstAmount).toFixed(2));
  return { ...li, amount, gstAmount, total };
}

function computeTotals(items: LineItem[]) {
  const subtotal = parseFloat(items.reduce((s, i) => s + i.amount, 0).toFixed(2));
  const totalGST = parseFloat(items.reduce((s, i) => s + i.gstAmount, 0).toFixed(2));
  const grandTotal = parseFloat((subtotal + totalGST).toFixed(2));
  return { subtotal, totalGST, grandTotal };
}

export default function QuotationFormClient() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const id = searchParams.get('id');
  const mode = searchParams.get('mode') || 'create';

  const [isViewMode, setIsViewMode] = useState(mode === 'view');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState<{ status: QuoteStatus; label: string } | null>(null);

  const [quoteNumber, setQuoteNumber] = useState('');
  const [date, setDate] = useState(todayDDMMYYYY());
  const [validUntil, setValidUntil] = useState(addDaysDDMMYYYY(todayDDMMYYYY(), 30));
  const [status, setStatus] = useState<QuoteStatus>('Draft');
  const [subject, setSubject] = useState('');
  const [preparedBy, setPreparedBy] = useState('Sanket Bhattacharya');
  const [clientName, setClientName] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientCity, setClientCity] = useState('');
  const [clientState, setClientState] = useState('');
  const [clientPIN, setClientPIN] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientGSTIN, setClientGSTIN] = useState('');
  const [gstType, setGstType] = useState<GSTType>('CGST+SGST');
  const [lineItems, setLineItems] = useState<LineItem[]>([createEmptyLineItem(1)]);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [notes, setNotes] = useState(DEFAULT_NOTES);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);

  const { subtotal, totalGST, grandTotal } = computeTotals(lineItems);
  const cgst = totalGST / 2;
  const sgst = totalGST / 2;

  useEffect(() => {
    if (id) {
      const q = getQuotationById(id);
      if (q) {
        setQuoteNumber(q.quoteNumber);
        setDate(q.date);
        setValidUntil(q.validUntil);
        setStatus(q.status);
        setSubject(q.subject);
        setPreparedBy(q.preparedBy);
        setClientName(q.clientName);
        setClientCompany(q.clientCompany);
        setClientAddress(q.clientAddress);
        setClientCity(q.clientCity);
        setClientState(q.clientState);
        setClientPIN(q.clientPIN);
        setClientPhone(q.clientPhone);
        setClientEmail(q.clientEmail);
        setClientGSTIN(q.clientGSTIN);
        setGstType(q.gstType);
        setLineItems(q.lineItems);
        setTerms(q.terms);
        setNotes(q.notes);
      }
    } else {
      setQuoteNumber(generateQuoteNumber());
    }
  }, [id]);

  useEffect(() => {
    setIsViewMode(mode === 'view');
  }, [mode]);

  useEffect(() => {
    setTemplates(getTemplates());
  }, []);

  const handleCityChange = (city: string) => {
    setClientCity(city);
    setGstType(detectGSTType(city));
  };

  const handleLoadTemplate = (templateId: string) => {
    if (!templateId) return;
    let tmpl;
    if (templateId === '__random__') {
      tmpl = templates[Math.floor(Math.random() * templates.length)];
    } else {
      tmpl = templates.find((t) => t.id === templateId);
    }
    if (!tmpl) return;
    const items: LineItem[] = tmpl.items.map((item, i) => {
      const li: LineItem = {
        id: `li-tmpl-${Date.now()}-${i}`,
        srNo: i + 1,
        description: item.description,
        hsn: item.hsn,
        qty: item.qty,
        unit: item.unit,
        rate: item.rate,
        gstRate: item.gstRate,
        amount: 0,
        gstAmount: 0,
        total: 0,
      };
      return computeLineItem(li);
    });
    setLineItems(items);
    setSelectedTemplate(tmpl.id);
    showToast(`Template "${tmpl.name}" loaded`, 'info');
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };
      if (field === 'hsn' && String(value) === '0602') {
        item.gstRate = 0;
      }
      updated[index] = computeLineItem(item);
      return updated;
    });
  };

  const handleCatalogueSelect = (index: number, catalogueId: string) => {
    const cat = CATALOGUE.find((c) => c.id === catalogueId);
    if (!cat) return;
    setLineItems((prev) => {
      const updated = [...prev];
      const item = {
        ...updated[index],
        description: cat.description,
        hsn: cat.hsn,
        gstRate: cat.gstRate,
        unit: cat.defaultUnit,
      };
      updated[index] = computeLineItem(item);
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, createEmptyLineItem(prev.length + 1)]);
  };

  const removeLineItem = (index: number) => {
    setLineItems((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((li, i) => ({ ...li, srNo: i + 1 }));
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!subject.trim()) errs.subject = 'Subject is required';
    if (!clientName.trim()) errs.clientName = 'Client name is required';
    if (!clientCity.trim()) errs.clientCity = 'Client city is required for GST determination';
    if (clientPhone && !/^(\+91\s?)?[6-9]\d{9}$/.test(clientPhone.replace(/\s/g, '')))
      errs.clientPhone = 'Enter a valid Indian mobile number';
    if (clientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail))
      errs.clientEmail = 'Enter a valid email address';
    if (clientGSTIN && !/^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z\d]$/.test(clientGSTIN))
      errs.clientGSTIN = 'GSTIN must be 15 characters in format: 22AAAAA0000A1Z5';
    lineItems.forEach((li, i) => {
      if (!li.description.trim()) errs[`li-${i}-desc`] = 'Description required';
      if (li.qty <= 0) errs[`li-${i}-qty`] = 'Qty must be > 0';
      if (li.rate <= 0) errs[`li-${i}-rate`] = 'Rate must be > 0';
      if (!VALID_GST_RATES.includes(li.gstRate)) errs[`li-${i}-gst`] = 'Invalid GST rate';
    });
    if (lineItems.length === 0) errs.lineItems = 'At least one line item is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildQuotation = (targetStatus: QuoteStatus): Quotation => ({
    id: id || generateId(),
    quoteNumber,
    date,
    validUntil,
    status: targetStatus,
    subject,
    preparedBy,
    clientName,
    clientCompany,
    clientAddress,
    clientCity,
    clientState,
    clientPIN,
    clientPhone,
    clientEmail,
    clientGSTIN,
    gstType,
    lineItems,
    subtotal,
    totalGST,
    grandTotal,
    terms,
    notes,
    createdAt: id ? (getQuotationById(id)?.createdAt || new Date().toISOString()) : new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const handleSave = async (targetStatus: QuoteStatus = status) => {
    if (!validate()) {
      showToast('Please fix the errors before saving', 'error');
      return;
    }
    setSaving(true);
    await new Promise((r) => setTimeout(r, 400));
    const q = buildQuotation(targetStatus);
    upsertQuotation(q);
    setStatus(targetStatus);
    setSaving(false);
    showToast(`${q.quoteNumber} saved as ${targetStatus}`, 'success');
    if (!id) {
      router.push(`/quotation-form?id=${q.id}&mode=edit`);
    }
  };

  const handleStatusChange = (newStatus: QuoteStatus) => {
    setShowStatusConfirm(null);
    const q = buildQuotation(newStatus);
    upsertQuotation(q);
    setStatus(newStatus);
    showToast(`Status updated to ${newStatus}`, 'success');
  };

  const handleDuplicate = () => {
    const today = todayDDMMYYYY();
    const newQuote: Quotation = {
      ...buildQuotation('Draft'),
      id: generateId(),
      quoteNumber: generateQuoteNumber(),
      date: today,
      validUntil: addDaysDDMMYYYY(today, 30),
      status: 'Draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lineItems: lineItems.map((li, i) => ({ ...li, id: `li-dup-${Date.now()}-${i}` })),
    };
    upsertQuotation(newQuote);
    showToast(`Duplicated as ${newQuote.quoteNumber}`, 'success');
    router.push(`/quotation-form?id=${newQuote.id}&mode=edit`);
  };

  // ── PDF Export using html2canvas + jsPDF ───────────────────────────────────
  const handleExportPDF = async () => {
    const printArea = document.getElementById('quotation-print-area');
    if (!printArea) {
      showToast('Preview not found. Please try again.', 'error');
      return;
    }

    setExporting(true);
    showToast('Generating PDF…', 'info');

    try {
      // Dynamically import to avoid SSR issues
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      // Force fixed width and wait for layout to stabilize
      const originalStyle = printArea.getAttribute('style') || '';
      printArea.style.width = '730px';
      printArea.style.maxWidth = '730px';
      printArea.style.overflow = 'visible';
      
      // Wait for layout to settle
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(printArea, {
        scale: 2,           // 2× for crisp 150dpi-equivalent output
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: printArea.offsetWidth,
        height: printArea.offsetHeight,
        windowWidth: 730,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('quotation-print-area');
          if (clonedElement) {
            clonedElement.style.width = '730px';
            clonedElement.style.maxWidth = '730px';
            clonedElement.style.overflow = 'visible';
          }
        }
      });
      
      // Restore original style
      printArea.setAttribute('style', originalStyle);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageW = pdf.internal.pageSize.getWidth();   // 210 mm
      const pageH = pdf.internal.pageSize.getHeight();  // 297 mm
      const margin = 8; // mm on each side
      const usableW = pageW - margin * 2;

      const canvasW = canvas.width;
      const canvasH = canvas.height;
      const ratio = canvasH / canvasW;

      const imgW = usableW;
      const imgH = imgW * ratio;

      // Multi-page support: split if taller than one A4 page
      const pageImgH = pageH - margin * 2;
      let yOffset = 0;
      let remainingH = imgH;
      let pageNum = 0;

      while (remainingH > 0) {
        if (pageNum > 0) pdf.addPage();

        const sliceH = Math.min(remainingH, pageImgH);
        // srcY in canvas pixels
        const srcY = (yOffset / imgH) * canvasH;
        const srcH = (sliceH / imgH) * canvasH;

        // Create a slice canvas
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvasW;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, srcY, canvasW, srcH, 0, 0, canvasW, srcH);
        }
        const sliceData = sliceCanvas.toDataURL('image/png');
        pdf.addImage(sliceData, 'PNG', margin, margin, imgW, sliceH, undefined, 'FAST');

        yOffset += sliceH;
        remainingH -= sliceH;
        pageNum++;
      }

      const filename = `TGI_${quoteNumber.replace(/\//g, '-')}_${clientName.replace(/\s+/g, '_') || 'Quote'}.pdf`;
      pdf.save(filename);
      showToast('PDF downloaded successfully!', 'success');
    } catch (err) {
      console.error('PDF export error:', err);
      showToast('PDF generation failed. Try the Print button instead.', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Fallback: browser print (properly isolated) ────────────────────────────
  const handlePrint = () => {
    const printArea = document.getElementById('quotation-print-area');
    if (!printArea) { window.print(); return; }

    // Remove stale portal if any
    const existing = document.getElementById('tgi-print-portal');
    if (existing) existing.remove();

    // Create portal with just the quotation HTML
    const portal = document.createElement('div');
    portal.id = 'tgi-print-portal';
    portal.style.cssText = 'position:fixed;top:0;left:0;width:100%;z-index:99999;background:#fff;display:none;';
    portal.appendChild(printArea.cloneNode(true));
    document.body.appendChild(portal);

    window.print();

    const cleanup = () => { portal.remove(); };
    window.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(cleanup, 5000);
  };

  if (isViewMode) {
    return (
      <>
        <div className="flex flex-col h-full">
          {/* View mode header */}
          <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 flex-wrap no-print">
            <div className="flex items-center gap-3">
              <Link href="/" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150">
                <ArrowLeft size={18} />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-primary">{quoteNumber}</span>
                  <StatusBadge status={status} />
                </div>
                <p className="text-sm text-muted-foreground mt-0.5 truncate max-w-xs">{subject || 'Untitled Quotation'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {status === 'Draft' && (
                <button
                  onClick={() => setShowStatusConfirm({ status: 'Sent', label: 'Mark as Sent' })}
                  className="btn-secondary text-blue-700 border-blue-200 hover:bg-blue-50"
                >
                  <Send size={15} />
                  Mark as Sent
                </button>
              )}
              {status === 'Sent' && (
                <>
                  <button
                    onClick={() => setShowStatusConfirm({ status: 'Approved', label: 'Approve' })}
                    className="btn-secondary text-green-700 border-green-200 hover:bg-green-50"
                  >
                    <CheckCircle size={15} />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowStatusConfirm({ status: 'Rejected', label: 'Reject' })}
                    className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  >
                    <XCircle size={15} />
                    Reject
                  </button>
                </>
              )}
              <button onClick={handleDuplicate} className="btn-secondary">
                <Copy size={15} />
                Duplicate
              </button>
              <button
                onClick={() => router.push(`/quotation-form?id=${id}&mode=edit`)}
                className="btn-secondary"
              >
                <Edit2 size={15} />
                Edit
              </button>
              {/* Primary: PDF download via html2canvas+jsPDF */}
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download size={15} />
                )}
                {exporting ? 'Generating…' : 'Export PDF'}
              </button>
              {/* Secondary: browser print fallback */}
              <button
                onClick={handlePrint}
                className="btn-secondary"
                title="Print / Save as PDF via browser"
              >
                <Printer size={15} />
                Print
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="flex-1 overflow-auto scrollbar-thin bg-background px-6 py-6">
            <QuotationPreview
              quotation={buildQuotation(status)}
              subtotal={subtotal}
              totalGST={totalGST}
              grandTotal={grandTotal}
              cgst={cgst}
              sgst={sgst}
            />
          </div>
        </div>

        <ConfirmModal
          isOpen={!!showStatusConfirm}
          title={`${showStatusConfirm?.label} Quotation`}
          message={`Are you sure you want to change the status of ${quoteNumber} to "${showStatusConfirm?.status}"?`}
          confirmLabel={showStatusConfirm?.label || 'Confirm'}
          onConfirm={() => showStatusConfirm && handleStatusChange(showStatusConfirm.status)}
          onCancel={() => setShowStatusConfirm(null)}
          variant="warning"
        />
        <ToastContainer />
      </>
    );
  }

  // Edit / Create mode
  return (
    <>
      <div className="flex flex-col h-full">
        {/* Form Header */}
        <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 flex-wrap no-print sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Link href="/" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-primary">{quoteNumber}</span>
                <StatusBadge status={status} />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {id ? 'Edit Quotation' : 'New Quotation'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {id && (
              <button
                onClick={() => router.push(`/quotation-form?id=${id}&mode=view`)}
                className="btn-secondary"
              >
                <Eye size={15} />
                Preview
              </button>
            )}
            {id && (
              <button onClick={handleDuplicate} className="btn-secondary">
                <Copy size={15} />
                Duplicate
              </button>
            )}
            <button
              onClick={() => handleSave('Draft')}
              disabled={saving}
              className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <FileText size={15} />
              )}
              Save Draft
            </button>
            <button
              onClick={() => handleSave(status)}
              disabled={saving}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={15} />
              )}
              Save Quotation
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="flex-1 overflow-auto scrollbar-thin px-6 py-6 space-y-5 max-w-screen-2xl">

          {/* Section 1: Quote Header */}
          <div className="form-section">
            <h2 className="form-section-title">
              <FileText size={16} className="text-primary" />
              Quotation Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Quote Number</label>
                <p className="text-xs text-muted-foreground mb-1">Auto-generated — not editable</p>
                <input type="text" value={quoteNumber} readOnly className="form-input-readonly font-mono" />
              </div>
              <div>
                <label className="form-label">Quote Date <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1">Format: DD/MM/YYYY</p>
                <input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="01/05/2026"
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
              <div>
                <label className="form-label">Valid Until <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1">Default: 30 days from quote date</p>
                <input
                  type="text"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  placeholder="31/05/2026"
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
              <div>
                <label className="form-label">Status</label>
                <p className="text-xs text-muted-foreground mb-1">Current workflow stage</p>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as QuoteStatus)}
                  className="form-select"
                  disabled={isViewMode}
                >
                  {(['Draft', 'Sent', 'Approved', 'Rejected'] as QuoteStatus[]).map((s) => (
                    <option key={`status-opt-${s}`} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 lg:col-span-2 xl:col-span-3">
                <label className="form-label">Subject / Project Title <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1">e.g. Terrace Garden Design & Installation – Sector 62, Noida</p>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Terrace Garden Design & Installation – ..."
                  className={`form-input ${errors.subject ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.subject && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.subject}</p>}
              </div>
              <div>
                <label className="form-label">Prepared By</label>
                <p className="text-xs text-muted-foreground mb-1">TGI staff member name</p>
                <input
                  type="text"
                  value={preparedBy}
                  onChange={(e) => setPreparedBy(e.target.value)}
                  placeholder="Staff name"
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>

          {/* Section 2: Client Details */}
          <div className="form-section">
            <h2 className="form-section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Client Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Client Name <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1">Full name of individual or contact person</p>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Arvind Mehta"
                  className={`form-input ${errors.clientName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.clientName && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.clientName}</p>}
              </div>
              <div>
                <label className="form-label">Company / Organisation</label>
                <p className="text-xs text-muted-foreground mb-1">Required for corporate/society clients</p>
                <input
                  type="text"
                  value={clientCompany}
                  onChange={(e) => setClientCompany(e.target.value)}
                  placeholder="Mehta Residences Pvt. Ltd."
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Address</label>
                <p className="text-xs text-muted-foreground mb-1">Street address, locality</p>
                <input
                  type="text"
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  placeholder="B-214, Sector 62"
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
              <div>
                <label className="form-label">City <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1">Used to determine IGST vs CGST+SGST</p>
                <input
                  type="text"
                  value={clientCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  placeholder="Noida"
                  className={`form-input ${errors.clientCity ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.clientCity && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.clientCity}</p>}
              </div>
              <div>
                <label className="form-label">State</label>
                <input
                  type="text"
                  value={clientState}
                  onChange={(e) => setClientState(e.target.value)}
                  placeholder="Uttar Pradesh"
                  className="form-input"
                  disabled={isViewMode}
                />
              </div>
              <div>
                <label className="form-label">PIN Code</label>
                <input
                  type="text"
                  value={clientPIN}
                  onChange={(e) => setClientPIN(e.target.value)}
                  placeholder="201301"
                  className="form-input"
                  maxLength={6}
                  disabled={isViewMode}
                />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <p className="text-xs text-muted-foreground mb-1">Format: +91 XXXXXXXXXX</p>
                <input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="+91 9876543210"
                  className={`form-input ${errors.clientPhone ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.clientPhone && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.clientPhone}</p>}
              </div>
              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className={`form-input ${errors.clientEmail ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.clientEmail && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.clientEmail}</p>}
              </div>
              <div>
                <label className="form-label">GSTIN</label>
                <p className="text-xs text-muted-foreground mb-1">15-char GST number — required for B2B invoices</p>
                <input
                  type="text"
                  value={clientGSTIN}
                  onChange={(e) => setClientGSTIN(e.target.value.toUpperCase())}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                  className={`form-input font-mono ${errors.clientGSTIN ? 'border-red-400 focus:ring-red-400' : ''}`}
                  disabled={isViewMode}
                />
                {errors.clientGSTIN && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.clientGSTIN}</p>}
              </div>
            </div>
          </div>

          {/* Section 3: GST Type + Template */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="form-section">
              <h2 className="form-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                GST Type
              </h2>
              <p className="text-xs text-muted-foreground mb-4">
                Auto-detected from client city. Delhi/NCR → CGST+SGST. Other states → IGST.
              </p>
              <div className="flex gap-3">
                {(['CGST+SGST', 'IGST'] as GSTType[]).map((type) => (
                  <button
                    key={`gst-type-${type}`}
                    onClick={() => !isViewMode && setGstType(type)}
                    disabled={isViewMode}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm font-semibold transition-all duration-150 ${
                      gstType === type
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/50 hover:bg-muted'
                    } disabled:cursor-not-allowed`}
                  >
                    {type}
                    <p className="text-xs font-normal mt-1 text-muted-foreground">
                      {type === 'CGST+SGST' ? 'Intra-state (Delhi/NCR)' : 'Inter-state (other states)'}
                    </p>
                  </button>
                ))}
              </div>
              {clientCity && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">
                  <Leaf size={12} className="text-primary" />
                  Auto-detected from <span className="font-semibold text-foreground">{clientCity}</span>:
                  <span className="font-semibold text-primary">{gstType}</span>
                </div>
              )}
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                Load Project Template
              </h2>
              <p className="text-xs text-muted-foreground mb-4">
                Pre-populate line items from a standard TGI project package. Existing items will be replaced.
              </p>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleLoadTemplate(e.target.value)}
                    className="form-select pr-8"
                    disabled={isViewMode}
                  >
                    <option value="">— Select a project template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
                <button
                  type="button"
                  onClick={() => !isViewMode && handleLoadTemplate('__random__')}
                  disabled={isViewMode}
                  title="Load a random project template"
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-muted hover:bg-primary/10 hover:border-primary hover:text-primary text-muted-foreground text-xs font-semibold transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
                  Random
                </button>
              </div>
              {selectedTemplate && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {templates.find((t) => t.id === selectedTemplate)?.description}
                </p>
              )}
            </div>
          </div>

          {/* Section 4: Line Items */}
          <div className="form-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="form-section-title mb-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                Line Items
                <span className="ml-2 text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
                </span>
              </h2>
              {!isViewMode && (
                <button onClick={addLineItem} className="btn-secondary text-xs py-1.5">
                  <Plus size={14} />
                  Add Item
                </button>
              )}
            </div>

            {errors.lineItems && (
              <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle size={14} />
                {errors.lineItems}
              </div>
            )}

            <div className="overflow-x-auto scrollbar-thin -mx-2 px-2">
              <table className="w-full min-w-[1100px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="table-th w-10">Sr.</th>
                    <th className="table-th w-64">Description / Item</th>
                    <th className="table-th w-24">HSN/SAC</th>
                    <th className="table-th w-20">Qty</th>
                    <th className="table-th w-24">Unit</th>
                    <th className="table-th w-28">Rate (₹)</th>
                    <th className="table-th w-20">GST %</th>
                    <th className="table-th w-28 text-right">Amount (₹)</th>
                    <th className="table-th w-28 text-right">GST Amt (₹)</th>
                    <th className="table-th w-28 text-right">Total (₹)</th>
                    {!isViewMode && <th className="table-th w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, index) => (
                    <LineItemRow
                      key={li.id}
                      item={li}
                      index={index}
                      isViewMode={isViewMode}
                      errors={errors}
                      onUpdate={updateLineItem}
                      onCatalogueSelect={handleCatalogueSelect}
                      onRemove={removeLineItem}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {!isViewMode && (
              <button
                onClick={addLineItem}
                className="mt-4 w-full py-2.5 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-150 flex items-center justify-center gap-2"
              >
                <Plus size={15} />
                Add another line item
              </button>
            )}
          </div>

          {/* Section 5: Terms, Notes & Totals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="form-section space-y-4">
              <div>
                <h2 className="form-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Terms & Conditions
                </h2>
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={5}
                  className="form-textarea text-xs leading-relaxed"
                  disabled={isViewMode}
                />
              </div>
              <div>
                <h2 className="form-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Notes
                </h2>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  className="form-textarea text-xs leading-relaxed"
                  disabled={isViewMode}
                />
              </div>
            </div>

            <div className="form-section">
              <h2 className="form-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                Totals
              </h2>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Subtotal (ex-GST)</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatINR(subtotal)}</span>
                </div>
                {gstType === 'CGST+SGST' ? (
                  <>
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-muted-foreground">CGST (9%)</span>
                      <span className="tabular-nums text-foreground text-sm">{formatINR(cgst)}</span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-sm text-muted-foreground">SGST (9%)</span>
                      <span className="tabular-nums text-foreground text-sm">{formatINR(sgst)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-sm text-muted-foreground">IGST (18%)</span>
                    <span className="tabular-nums text-foreground text-sm">{formatINR(totalGST)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">Total GST</span>
                  <span className="tabular-nums text-foreground text-sm font-medium">{formatINR(totalGST)}</span>
                </div>
                <div className="flex items-center justify-between py-3 bg-primary/10 rounded-xl px-4 -mx-1 mt-2">
                  <span className="text-base font-bold text-foreground">Grand Total</span>
                  <span className="text-xl font-bold tabular-nums text-primary">{formatINR(grandTotal)}</span>
                </div>
                {grandTotal > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1 font-medium">Amount in Words</p>
                    <p className="text-xs text-foreground font-medium leading-relaxed bg-muted rounded-lg px-3 py-2 italic">
                      {amountInWords(grandTotal)}
                    </p>
                  </div>
                )}
                {lineItems.some((li) => li.gstRate > 0) && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">GST Breakup by Rate</p>
                    <div className="space-y-1">
                      {VALID_GST_RATES.filter((r) => lineItems.some((li) => li.gstRate === r && li.gstAmount > 0)).map((rate) => {
                        const taxable = lineItems.filter((li) => li.gstRate === rate).reduce((s, li) => s + li.amount, 0);
                        const gstAmt = lineItems.filter((li) => li.gstRate === rate).reduce((s, li) => s + li.gstAmount, 0);
                        return (
                          <div key={`gst-breakup-${rate}`} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">@{rate}% on {formatINR(taxable)}</span>
                            <span className="tabular-nums text-foreground font-medium">{formatINR(gstAmt)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save Actions Footer */}
          {!isViewMode && (
            <div className="bg-card border border-border rounded-xl px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">
                {Object.keys(errors).length > 0 && (
                  <span className="flex items-center gap-1.5 text-red-600">
                    <AlertCircle size={14} />
                    {Object.keys(errors).length} validation error{Object.keys(errors).length > 1 ? 's' : ''} — please fix before saving
                  </span>
                )}
              </p>
              <div className="flex items-center gap-3">
                <Link href="/" className="btn-secondary">Cancel</Link>
                <button
                  onClick={() => handleSave('Draft')}
                  disabled={saving}
                  className="btn-secondary disabled:opacity-60"
                >
                  {saving ? <span className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" /> : <FileText size={15} />}
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSave(status)}
                  disabled={saving}
                  className="btn-primary disabled:opacity-60"
                >
                  {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                  Save Quotation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </>
  );
}

// ─── Line Item Row ───────────────────────────────────────────────────────────
interface LineItemRowProps {
  item: LineItem;
  index: number;
  isViewMode: boolean;
  errors: Record<string, string>;
  onUpdate: (index: number, field: keyof LineItem, value: string | number) => void;
  onCatalogueSelect: (index: number, catalogueId: string) => void;
  onRemove: (index: number) => void;
}

function LineItemRow({ item, index, isViewMode, errors, onUpdate, onCatalogueSelect, onRemove }: LineItemRowProps) {
  const [catSearch, setCatSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredCat = catSearch.length >= 1
    ? CATALOGUE.filter(
        (c) =>
          c.description.toLowerCase().includes(catSearch.toLowerCase()) ||
          c.hsn.includes(catSearch) ||
          c.category.toLowerCase().includes(catSearch.toLowerCase())
      ).slice(0, 12)
    : [];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 group transition-colors duration-100">
      <td className="table-td text-center text-xs text-muted-foreground font-semibold">{item.srNo}</td>

      <td className="table-td">
        {isViewMode ? (
          <span className="text-sm">{item.description}</span>
        ) : (
          <div className="relative" ref={dropdownRef}>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={catSearch || item.description}
                onChange={(e) => {
                  setCatSearch(e.target.value);
                  onUpdate(index, 'description', e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => { setCatSearch(''); setShowDropdown(true); }}
                placeholder="Type to search catalogue..."
                className={`form-input py-1.5 text-xs ${errors[`li-${index}-desc`] ? 'border-red-400' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-1.5 rounded border border-border hover:bg-muted transition-colors flex-shrink-0"
              >
                <Search size={12} className="text-muted-foreground" />
              </button>
            </div>
            {errors[`li-${index}-desc`] && (
              <p className="text-xs text-red-600 mt-0.5">{errors[`li-${index}-desc`]}</p>
            )}
            {showDropdown && filteredCat.length > 0 && (
              <div className="absolute z-50 top-full left-0 mt-1 w-80 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-56 overflow-y-auto scrollbar-thin">
                  {filteredCat.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        onCatalogueSelect(index, cat.id);
                        setCatSearch('');
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0"
                    >
                      <p className="text-xs font-semibold text-foreground leading-tight">{cat.description}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground font-mono">HSN: {cat.hsn}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">GST: {cat.gstRate}%</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-primary">{cat.category}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </td>

      <td className="table-td">
        {isViewMode ? (
          <span className="font-mono text-xs">{item.hsn}</span>
        ) : (
          <input
            type="text"
            value={item.hsn}
            onChange={(e) => onUpdate(index, 'hsn', e.target.value)}
            placeholder="HSN/SAC"
            className="form-input py-1.5 text-xs font-mono w-20"
          />
        )}
      </td>

      <td className="table-td">
        {isViewMode ? (
          <span className="tabular-nums text-sm">{item.qty}</span>
        ) : (
          <input
            type="number"
            value={item.qty}
            onChange={(e) => onUpdate(index, 'qty', parseFloat(e.target.value) || 0)}
            min="0"
            step="0.01"
            className={`form-input py-1.5 text-xs w-16 tabular-nums ${errors[`li-${index}-qty`] ? 'border-red-400' : ''}`}
          />
        )}
      </td>

      <td className="table-td">
        {isViewMode ? (
          <span className="text-sm">{item.unit}</span>
        ) : (
          <select
            value={item.unit}
            onChange={(e) => onUpdate(index, 'unit', e.target.value)}
            className="form-select py-1.5 text-xs w-20"
          >
            {VALID_UNITS.map((u) => (
              <option key={`unit-${u}`} value={u}>{u}</option>
            ))}
          </select>
        )}
      </td>

      <td className="table-td">
        {isViewMode ? (
          <span className="tabular-nums text-sm">{formatINR(item.rate)}</span>
        ) : (
          <input
            type="number"
            value={item.rate}
            onChange={(e) => onUpdate(index, 'rate', parseFloat(e.target.value) || 0)}
            min="0"
            step="0.01"
            className={`form-input py-1.5 text-xs w-24 tabular-nums ${errors[`li-${index}-rate`] ? 'border-red-400' : ''}`}
          />
        )}
      </td>

      <td className="table-td">
        {isViewMode ? (
          <span className="text-sm">{item.gstRate}%</span>
        ) : (
          <select
            value={item.gstRate}
            onChange={(e) => onUpdate(index, 'gstRate', parseInt(e.target.value))}
            disabled={item.hsn === '0602'}
            className="form-select py-1.5 text-xs w-16 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {VALID_GST_RATES.map((r) => (
              <option key={`gst-rate-${r}`} value={r}>{r}%</option>
            ))}
          </select>
        )}
      </td>

      <td className="table-td text-right">
        <span className="tabular-nums text-sm font-medium text-foreground">{formatINR(item.amount)}</span>
      </td>
      <td className="table-td text-right">
        <span className="tabular-nums text-sm text-muted-foreground">{formatINR(item.gstAmount)}</span>
      </td>
      <td className="table-td text-right">
        <span className="tabular-nums text-sm font-bold text-primary">{formatINR(item.total)}</span>
      </td>

      {!isViewMode && (
        <td className="table-td">
          <button
            type="button"
            onClick={() => onRemove(index)}
            title="Remove this line item"
            className="p-1.5 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-all duration-150 opacity-0 group-hover:opacity-100"
          >
            <Trash2 size={13} />
          </button>
        </td>
      )}
    </tr>
  );
}
